import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, inject, viewChild } from '@angular/core';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { ActivatedRoute, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { combineLatest, filter, first, map, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { LoanPurposeType, LoanStatus, MortgageType, OriginationChannel, RefinancePurpose } from '../../../cmg-1003/models';
import { SessionQuery, softLockStatuses } from '../../../core/state';
import { AusQuery, Cmg1003EmploymentExpensesQuery, Cmg1003FileDataQuery, Cmg1003LoanQuery, Cmg1003StoreQueryService } from '../../../loan-state';
import { SyncType } from '../../../models/global.models';
import { Features, PermissionType } from '../../../shared/models/permission/permission.model';
import { PermissionService } from '../../../shared/services/permission.service';
import { FeatureFlagRepository, FeatureName } from '../../state/feature-flag.repository';
import { AppSettingsService } from '../../../shared';
import { GlobalNavService } from '../../../shared/services/global-nav.service';
import { MenuItem } from 'primeng/api';
import { PartyMiscQuery } from '../../../loan-state/party-misc/party-misc.query';
import { NgClass, AsyncPipe } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';

interface MenuOption {
  displayName: string;
  link?: string;
  tooltip?: string;
  hasSubmenu?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  submenu?: MenuOption[];
}

@Component({
  selector: 'cmg-clear2-ui-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, Drawer, NgClass, RouterLinkActive, RouterLink, Tooltip, AsyncPipe],
})
export class SidebarMenuComponent implements OnDestroy {
  private readonly _featureFlagRepo = inject(FeatureFlagRepository);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _permissionService = inject(PermissionService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _appSettingsService = inject(AppSettingsService);
  private readonly _globalNavService = inject(GlobalNavService);
  private readonly _partyMiscQuery = inject(PartyMiscQuery);
  private readonly _ausQuery = inject(AusQuery);
  private readonly _employmentExpenses = inject(Cmg1003EmploymentExpensesQuery);

  readonly drawerElement = viewChild<Drawer>('menuDrawer');
  readonly menuButton = viewChild<Button>('menuButton');
  visible = false;
  menuOptions$ = this._getMenuOptions().pipe(
    tap((menuOptions) => {
      this._updateGlobalNav(menuOptions);
    }),
  );
  isSandboxLoan$ = this._storeQuery.isSandboxLoan$;

  private readonly _destroyed$ = new Subject<boolean>();

  constructor() {
    this._router.events.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.visible = false;
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
    this._globalNavService.clearExtraNavItems();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Skip if drawer isn't visible
    if (!this.visible) {
      return;
    }

    // Check if click is outside both the drawer and the toggle button
    const drawerEl = this.drawerElement()?.container;
    const buttonEl = this.menuButton()?.el?.nativeElement;

    const clickedInDrawer = drawerEl && drawerEl.contains(event.target as Node);
    const clickedOnButton = buttonEl && buttonEl.contains(event.target as Node);

    if (!clickedInDrawer && !clickedOnButton) {
      this.visible = false;
      this._cdRef.markForCheck();
    }
  }

  private _filterMenuOptions(menuOptions: MenuOption[], displayNames: string[], includeSubmenus = false): MenuOption[] {
    if (displayNames && displayNames.length > 0) {
      let updatedMenuOptions = menuOptions.filter((item) => !displayNames.includes(item.displayName));
      if (includeSubmenus) {
        updatedMenuOptions = updatedMenuOptions.map((item) => {
          if (item.submenu && item.submenu.length > 0) {
            return {
              ...item,
              submenu: item.submenu.filter((item) => !displayNames.includes(item.displayName)),
            };
          } else {
            return item;
          }
        });
      }
      return updatedMenuOptions;
    }
  }

  /**
   * Items are mapped in this component in order to specify the activated route. This
   * is important because without it, the global nav menu does not have the context
   * for relative links
   * @param menuOptions
   * @private
   */
  private _mapMenuOptionsToMenuItems(menuOptions: MenuOption[]): MenuItem[] {
    return menuOptions.map((item) => {
      return {
        label: item.displayName,
        items: item.submenu ? this._mapMenuOptionsToMenuItems(item.submenu) : undefined,
        routerLink: item.link,
        visible: item.hidden !== true,
        disabled: item.disabled === true,
        state: { route: this._route },
      };
    });
  }

  /**
   * Update the global navigation menu with extra menu items
   * @param menuOptions
   * @private
   */
  private _updateGlobalNav(menuOptions: MenuOption[] | null) {
    if (menuOptions) {
      this._globalNavService.setExtraNavItems(this._mapMenuOptionsToMenuItems(menuOptions));
    } else {
      this._globalNavService.clearExtraNavItems();
    }
  }

  private _isTempBuydownHidden(
    mortgageType: MortgageType,
    loanPurpose: LoanPurposeType,
    refinancePurpose: RefinancePurpose,
    loanStatusId: LoanStatus,
  ): boolean {
    const visibleRefinancePurposes = [
      RefinancePurpose.CashOutLimited,
      RefinancePurpose.NoCashOutFREOwnedRefinance,
      RefinancePurpose.NoCashOutOther,
      RefinancePurpose.NoCashOutStreamlinedRefinance,
      RefinancePurpose.ChangeInRateTerm,
    ];
    const isVisibleForForRefiLoans =
      mortgageType === MortgageType.conventional && loanPurpose === LoanPurposeType.refinance && visibleRefinancePurposes.includes(refinancePurpose);
    if (loanPurpose === LoanPurposeType.purchase || isVisibleForForRefiLoans) {
      return !softLockStatuses.includes(loanStatusId);
    } else {
      return true;
    }
  }

  private _getMenuOptions() {
    const isUSDA$ = this._loanQuery.select((t) => t.mortgageAppliedFor === MortgageType.usda);
    const isVoeStandaloneEnabled$ = this._employmentExpenses.select().pipe(
      filter((employeeExpenses) => !!employeeExpenses.voe),
      map((employeeExpenses) => employeeExpenses.voe?.voeCascadeStandaloneEnabled),
      first(),
    );

    return this._sessionQuery
      .select((state) => state.all1003Loaded)
      .pipe(
        filter((loaded) => loaded),
        switchMap(() =>
          combineLatest([
            this._loanQuery.select(['fileDataId', 'mortgageAppliedFor']),
            this._loanQuery.selectLoanOriginationChannel(),
            this._partyMiscQuery.select((state) => state.servicerCode),
            isVoeStandaloneEnabled$,
            isUSDA$,
            this._featureFlagRepo.isFeatureEnabled$(FeatureName.CCW2),
            this._featureFlagRepo.isFeatureEnabled$(FeatureName.Disclosures),
            this._featureFlagRepo.isFeatureEnabled$(FeatureName.ConditionsPortal),
            this._featureFlagRepo.isFeatureEnabled$(FeatureName.ServicingLoanData),
            this._featureFlagRepo.isFeatureEnabled$(FeatureName.SandboxLoanMVP2),
            this._fileDataQuery.select((f) => f.syncType),
            this._storeQuery.isSandboxLoan$,
            this._appSettingsService.isShowSandboxLoanLpa$,
          ]).pipe(
            map(
              ([
                { fileDataId, mortgageAppliedFor: mortgageType },
                originationChannel,
                servicerCode,
                isVoeStandaloneEnabled,
                isUsda,
                isFeatureEnabledCCW2,
                isFeatureEnabledDisclosures,
                isFeatureEnabledConditionsPortal,
                isFeatureEnabledServicingLoanData,
                isFeatureEnabledSandboxLoanMVP2,
                syncType,
                isSandboxLoan,
                isShowSandboxLoanLpa,
              ]) => {
                let menuOptions: MenuOption[] = [
                  {
                    displayName: 'Loan Summary',
                    link: './loansummary',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'URLA',
                    link: `/urla2/${fileDataId}`,
                    hasSubmenu: false,
                  },
                  ...(mortgageType === MortgageType.fha
                    ? [
                        {
                          displayName: 'FHA Addendum',
                          link: './fhaaddendum',
                          hasSubmenu: false,
                        },
                      ]
                    : []),
                  ...(mortgageType === MortgageType.va
                    ? [
                        {
                          displayName: 'VA Information',
                          link: './va-2',
                          hasSubmenu: false,
                        },
                      ]
                    : []),
                  {
                    displayName: 'AIO',
                    link: './aio',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Temporary Buydown',
                    link: './buydown',
                    hasSubmenu: false,
                    hidden: false,
                  },
                  {
                    displayName: '1008',
                    link: './1008',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Credit',
                    link: './credit',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Verifications',
                    hasSubmenu: true,
                    submenu: [
                      {
                        displayName: 'VOE',
                        link: './voe',
                      },
                      {
                        displayName: 'VOA',
                        link: './voa',
                      },
                      ...(isVoeStandaloneEnabled
                        ? [
                            {
                              displayName: 'VOE Cascade',
                              link: './voeCascade',
                            },
                          ]
                        : []),
                    ],
                  },
                  {
                    displayName: 'Closing Costs',
                    link: '',
                    hasSubmenu: true,
                    submenu: [
                      {
                        displayName: 'Closing Cost Worksheet',
                        link: './closing-cost-worksheet-1-5',
                        tooltip: 'Closing Cost Worksheet 1.5',
                      },
                      ...(isFeatureEnabledCCW2
                        ? [
                            {
                              displayName: 'Closing Cost Worksheet 2.0',
                              link: './closing-cost-worksheet-2-0',
                            },
                          ]
                        : []),
                      {
                        displayName: 'Fee Worksheet',
                        link: './fee-worksheet',
                      },
                    ],
                  },
                  {
                    displayName: 'Product & Pricing',
                    link: './pricing',
                    hasSubmenu: true,
                    submenu: [
                      {
                        displayName: 'Price / Rate Lock',
                        link: './pricing',
                      },
                      {
                        displayName: 'Pricing Confirmation',
                        link: 'pricing-confirmation',
                      },
                      {
                        displayName: 'Pricing History',
                        link: './pricing-history',
                      },
                    ],
                  },
                  {
                    displayName: 'AUS',
                    link: './aus',
                    hasSubmenu: true,
                    submenu: [
                      {
                        displayName: isFeatureEnabledSandboxLoanMVP2 || isShowSandboxLoanLpa ? 'DU / LPA / DU EA' : 'DU / DU EA',
                        link: './aus',
                        disabled: isUsda,
                      },
                      ...(isFeatureEnabledSandboxLoanMVP2
                        ? [
                            {
                              displayName: 'AUS History',
                              link: './aus-history',
                            },
                          ]
                        : []),
                    ],
                  },
                  {
                    displayName: '4506-C',
                    link: './4506c',
                    hasSubmenu: false,
                    disabled: syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien,
                    tooltip:
                      syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien
                        ? '4506-C is not applicable on synced 2nd or 3rd loans'
                        : undefined,
                  },
                  {
                    displayName: 'Parties',
                    link: './parties',
                    hasSubmenu: false,
                  },
                  ...(isFeatureEnabledDisclosures
                    ? [
                        {
                          displayName: 'Disclosures',
                          link: './disclosures',
                          hasSubmenu: false,
                          disabled: syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien,
                        },
                      ]
                    : []),
                  {
                    displayName: 'Document Management',
                    link: 'documents/management',
                    hasSubmenu: true,
                    submenu: [
                      {
                        displayName: 'Doc Management',
                        link: 'documents/management',
                      },
                      {
                        displayName: 'Request Docs',
                        link: 'documents/request',
                      },
                      {
                        displayName: 'Pending Docs',
                        link: 'documents/pending',
                      },
                      {
                        displayName: 'Review Docs',
                        link: 'documents/review',
                      },
                    ],
                  },
                  {
                    displayName: 'Cover Letter/Submission Notes',
                    link: './coverletter',
                    hasSubmenu: false,
                  },
                  ...(isFeatureEnabledConditionsPortal
                    ? [
                        {
                          displayName: 'Conditions Portal',
                          link: './conditions-portal',
                          hasSubmenu: false,
                        },
                      ]
                    : []),
                  {
                    displayName: 'Conditions',
                    link: './conditions',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Conversation Log',
                    link: './conversationlog',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Letter Generation',
                    link: './letter',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Important Dates',
                    link: './importantdate',
                    hasSubmenu: false,
                  },
                  {
                    displayName: 'Loan History Snapshot',
                    link: './loan-history-snapshot',
                    hasSubmenu: false,
                  },
                  ...(isFeatureEnabledServicingLoanData && servicerCode?.trim().toLocaleLowerCase() === 'cmg-loanserv'
                    ? [
                        {
                          displayName: 'Servicing',
                          link: './servicing',
                          hasSubmenu: false,
                        },
                      ]
                    : []),
                ];

                // Bulk Filtering For Permissions
                if (!this._permissionService.hasPermission(Features.Loan, PermissionType.View)) {
                  menuOptions = menuOptions.filter((item) => {
                    return item.displayName !== 'URLA';
                  });
                }

                // Bulk Filtering For Synced Loans
                if (syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien) {
                  const pagesToHide = [
                    'Temporary Buydown',
                    'Verifications',
                    'Product & Pricing',
                    'Closing Costs',
                    'AUS',
                    'Disclosures',
                    'Letter Generation',
                    'Parties',
                  ];
                  menuOptions = this._filterMenuOptions(menuOptions, pagesToHide);
                }

                // Bulk Filtering For Brokered Out Loans
                if (originationChannel === OriginationChannel.brokeredOut) {
                  menuOptions = menuOptions.filter((item) => {
                    return item.displayName !== 'Letter Generation';
                  });
                }

                // Bulk Filtering For Sandbox Loans
                if (isSandboxLoan) {
                  const itemsToHide = [
                    'Verifications',
                    'Closing Cost Worksheet 2.0',
                    'Fee Worksheet',
                    'Pricing Confirmation',
                    'Pricing History',
                    'Disclosures',
                    '4506-C',
                    'Document Management',
                    'Conditions',
                    'Loan History Snapshot',
                    'Parties',
                    'Cover Letter/Submission Notes',
                    'Conversation Log',
                    'Letter Generation',
                    'Important Dates',
                  ];

                  if (!isFeatureEnabledSandboxLoanMVP2) {
                    itemsToHide.push('AUS');
                    itemsToHide.push('Closing Costs');
                    itemsToHide.push('Closing Cost Worksheet');
                    itemsToHide.push('Closing Cost Worksheet 1.5');
                  }

                  menuOptions = this._filterMenuOptions(menuOptions, itemsToHide, true);
                }

                return menuOptions;
              },
            ),
          ),
        ),
      );
  }
}
